/**
 * Ingest Document — modal lifecycle + post-upload dispatch.
 *
 * Triggered from the plus menu. Lets the user pick a file (PDF/DOCX/
 * TXT/MD), choose Analyze or Read aloud, and optionally add special
 * instructions for analyze mode.
 *
 * Server contract: POST /document/extract (multipart) does the text
 * extraction, schedules fact extraction, and — for read_aloud mode —
 * saves the document text as an assistant message with full DB
 * persistence. This module renders the bubbles + dispatches to either
 * autoPlayNarratorTts() or sendMessageWithAttachments() based on mode.
 */
import { state } from './state.js';
import { getHeaders } from './api.js';
import { addMessage, addSystemMessage, renderFactIngestChip } from './ui.js';
import { sendMessageWithAttachments } from './chat.js';
import { autoPlayNarratorTts, showIngestPlaybackUI } from './voice.js';

const MAX_BYTES = 50 * 1024 * 1024;
const ACCEPTED_EXTS = ['.pdf', '.docx', '.txt', '.md'];

let modalEl = null;
let dropzoneEl = null;
let fileInputEl = null;
let fileNameEl = null;
let fileSizeEl = null;
let fileClearEl = null;
let dropzoneEmptyEl = null;
let dropzoneFileEl = null;
let modeRadios = null;
let instructionsTextarea = null;
let lockToggleWrap = null;
let lockCheckbox = null;
let extractFactsToggleWrap = null;
let extractFactsCheckbox = null;
let errorEl = null;
let submitBtn = null;
let cancelBtn = null;
let closeBtn = null;

let selectedFile = null;
let initialized = false;

function _cacheElements() {
    modalEl = document.getElementById('ingest-modal');
    dropzoneEl = document.getElementById('ingest-dropzone');
    fileInputEl = document.getElementById('ingest-file-input');
    fileNameEl = document.getElementById('ingest-file-name');
    fileSizeEl = document.getElementById('ingest-file-size');
    fileClearEl = document.getElementById('ingest-file-clear');
    dropzoneEmptyEl = dropzoneEl?.querySelector('.ingest-dropzone-empty');
    dropzoneFileEl = dropzoneEl?.querySelector('.ingest-dropzone-file');
    modeRadios = modalEl?.querySelectorAll('input[name="ingest-mode"]');
    instructionsTextarea = document.getElementById('ingest-instructions');
    lockToggleWrap = document.getElementById('ingest-lock-toggle');
    lockCheckbox = document.getElementById('ingest-lock-screen');
    extractFactsToggleWrap = document.getElementById('ingest-readaloud-extract-toggle');
    extractFactsCheckbox = document.getElementById('ingest-readaloud-extract');
    errorEl = document.getElementById('ingest-error');
    submitBtn = document.getElementById('ingest-modal-submit');
    cancelBtn = document.getElementById('ingest-modal-cancel');
    closeBtn = document.getElementById('ingest-modal-close');
}

function _formatSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function _setError(msg) {
    if (!errorEl) return;
    if (msg) {
        errorEl.textContent = msg;
        errorEl.hidden = false;
    } else {
        errorEl.textContent = '';
        errorEl.hidden = true;
    }
}

function _setFile(file) {
    selectedFile = file;
    if (!file) {
        dropzoneEmptyEl.hidden = false;
        dropzoneFileEl.hidden = true;
        submitBtn.disabled = true;
        return;
    }
    fileNameEl.textContent = file.name;
    fileSizeEl.textContent = _formatSize(file.size);
    dropzoneEmptyEl.hidden = true;
    dropzoneFileEl.hidden = false;
    submitBtn.disabled = false;
    _setError('');
}

function _validateAndSet(file) {
    if (!file) return;
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ACCEPTED_EXTS.includes(ext)) {
        _setError(`Unsupported file type "${ext}". Allowed: ${ACCEPTED_EXTS.join(', ')}`);
        return;
    }
    if (file.size > MAX_BYTES) {
        _setError(`File too large (${_formatSize(file.size)}). Limit is 50 MB.`);
        return;
    }
    _setFile(file);
}

function _currentMode() {
    for (const r of modeRadios) if (r.checked) return r.value;
    return 'analyze';
}

function _updateInstructionsState() {
    // Special instructions are analyze-only. Lock-screen + extract-facts
    // toggles are read-aloud only. Toggle visibility/disabled state so the
    // user gets a clear cue about what's relevant for the picked mode.
    const mode = _currentMode();
    if (instructionsTextarea) {
        instructionsTextarea.disabled = (mode !== 'analyze');
    }
    if (lockToggleWrap) {
        lockToggleWrap.classList.toggle('is-hidden', mode !== 'read_aloud');
    }
    if (extractFactsToggleWrap) {
        extractFactsToggleWrap.classList.toggle('is-hidden', mode !== 'read_aloud');
    }
}

function _resetState() {
    _setFile(null);
    if (fileInputEl) fileInputEl.value = '';
    if (instructionsTextarea) instructionsTextarea.value = '';
    if (modeRadios && modeRadios.length) modeRadios[0].checked = true;  // analyze default
    if (lockCheckbox) lockCheckbox.checked = false;
    if (extractFactsCheckbox) extractFactsCheckbox.checked = false;
    _updateInstructionsState();
    _setError('');
}

function _wire() {
    if (initialized) return;
    _cacheElements();
    if (!modalEl) return;
    initialized = true;

    // Dropzone — click to pick, drag to drop, keyboard enter
    dropzoneEl.addEventListener('click', (e) => {
        // Don't re-open the picker when clicking the clear button
        if (e.target.closest('.ingest-file-clear')) return;
        fileInputEl?.click();
    });
    dropzoneEl.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            fileInputEl?.click();
        }
    });
    ['dragenter', 'dragover'].forEach(evt => {
        dropzoneEl.addEventListener(evt, (e) => {
            e.preventDefault();
            dropzoneEl.classList.add('drag-over');
        });
    });
    ['dragleave', 'drop'].forEach(evt => {
        dropzoneEl.addEventListener(evt, (e) => {
            e.preventDefault();
            dropzoneEl.classList.remove('drag-over');
        });
    });
    dropzoneEl.addEventListener('drop', (e) => {
        const f = e.dataTransfer?.files?.[0];
        if (f) _validateAndSet(f);
    });

    fileInputEl.addEventListener('change', (e) => {
        const f = e.target.files?.[0];
        if (f) _validateAndSet(f);
    });

    fileClearEl.addEventListener('click', (e) => {
        e.stopPropagation();
        _setFile(null);
        fileInputEl.value = '';
    });

    // Mode change → gray out instructions if read aloud
    modeRadios.forEach(r => {
        r.addEventListener('change', _updateInstructionsState);
    });

    // Close handlers — X, Cancel, click-outside, Escape
    closeBtn.addEventListener('click', closeIngestModal);
    cancelBtn.addEventListener('click', closeIngestModal);
    modalEl.addEventListener('click', (e) => {
        if (e.target === modalEl) closeIngestModal();
    });
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && modalEl.classList.contains('active')) {
            closeIngestModal();
        }
    });

    submitBtn.addEventListener('click', _handleSubmit);
}

export function openIngestModal() {
    _wire();
    if (!modalEl) return;
    _resetState();
    modalEl.classList.remove('hidden');
    // Force layout commit before adding .active so the opacity/scale
    // transition actually plays (matches the redo-modal show pattern).
    requestAnimationFrame(() => {
        modalEl.classList.add('active');
        // Focus the dropzone so keyboard users can immediately Enter to
        // open the file picker.
        dropzoneEl?.focus();
    });
}

export function closeIngestModal() {
    if (!modalEl) return;
    modalEl.classList.remove('active');
    // Match the modal-overlay transition duration so the fade-out plays
    // before the element becomes display:none via .hidden.
    setTimeout(() => modalEl.classList.add('hidden'), 220);
}

async function _handleSubmit() {
    if (!selectedFile) return;
    submitBtn.disabled = true;
    cancelBtn.disabled = true;
    _setError('');
    const originalLabel = submitBtn.textContent;
    submitBtn.textContent = 'Working…';

    const mode = _currentMode();
    const instructions = (instructionsTextarea.value || '').trim();
    const wantsLockScreen = !!(lockCheckbox && lockCheckbox.checked);
    // Background fact extraction (the server's fire-and-forget pass).
    //   analyze              → always on (the model will use the facts
    //                          immediately in its commentary).
    //   read_aloud           → opt-in via the new "Also extract facts"
    //                          checkbox; the server uses the document-
    //                          mode prompt for this path so the input is
    //                          treated as a document, not a chat turn.
    //   extract_facts_only   → off here; the server runs its own
    //                          synchronous extraction and returns the
    //                          chip payload — a second background pass
    //                          would just duplicate work.
    const extractFacts =
        mode === 'analyze' ||
        (mode === 'read_aloud' && !!(extractFactsCheckbox && extractFactsCheckbox.checked));

    try {
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('mode', mode);
        formData.append('extract_facts', extractFacts ? 'true' : 'false');
        if (state.sessionId) formData.append('session_id', state.sessionId);
        if (state.loadedAgent) formData.append('agent', state.loadedAgent);
        if (state.loadedCollection) formData.append('collection', state.loadedCollection);
        if (state.loadedModel) formData.append('model', state.loadedModel);

        // getHeaders(false) → omit Content-Type so the browser sets the
        // multipart boundary correctly.
        const res = await fetch('/document/extract', {
            method: 'POST',
            headers: getHeaders(false),
            body: formData,
        });
        if (!res.ok) {
            let detail = `Upload failed (${res.status})`;
            try {
                const j = await res.json();
                if (j?.detail) detail = j.detail;
            } catch (_) { /* swallow */ }
            throw new Error(detail);
        }
        const data = await res.json();

        // Server may have created a new session — keep client state aligned.
        if (data.session_id && !state.sessionId) {
            state.sessionId = data.session_id;
        }

        // Close modal before continuing so the user sees the chat updating.
        closeIngestModal();

        if (mode === 'extract_facts_only') {
            // No user bubble, no assistant bubble — just the chip. The
            // server already extracted facts synchronously and saved them
            // tagged with chip.source_marker, plus persisted the chip as
            // a system-typed message so a refresh/reload restores it via
            // the history loader.
            if (data.chip) {
                renderFactIngestChip(data.chip, { sessionId: data.session_id || state.sessionId });
            }
            return;
        }

        // Render the user-side document bubble. The server already saved
        // the message; pass messageId so the standard delete/redo wiring
        // gets attached (orphan-delete detection will leave it alone since
        // the read_aloud path has an assistant message after, and analyze
        // gets one streamed in below).
        addMessage('user', data.text, {
            header: 'You',
            isDocument: true,
            filename: data.filename,
            messageId: data.user_message_id,
        });

        if (mode === 'read_aloud') {
            // Server saved the assistant bubble already. Render it with
            // the messageId so it picks up the standard action bar (TTS
            // replay / redo / delete). We deliberately pass extracting
            // as false even if the server scheduled one — read-aloud
            // disables extraction client-side (see extractFacts above)
            // so the spinner never appears and the footer is immediate.
            const assistantName = _assistantName();
            addMessage('assistant', data.text, {
                header: assistantName,
                render: true,
                messageId: data.message_id,
                extracting: false,
            });
            // Show the voice UI in TTS-only playback config (pause +
            // mute + close, no chunk-nav). Has to happen BEFORE
            // autoPlayNarratorTts so the pause button is wired and
            // visible by the time the state transitions to speaking.
            showIngestPlaybackUI();
            // Auto-play once. The footer TTS button can replay on demand.
            try {
                autoPlayNarratorTts(data.text);
            } catch (e) {
                console.error('[Ingest] autoPlay failed:', e);
            }
            // Lock screen is opt-in via the modal toggle. The existing
            // slide-to-unlock handler in main.js dismisses it on user
            // action.
            if (wantsLockScreen) {
                const lockOverlay = document.getElementById('narrator-lock-overlay');
                if (lockOverlay) lockOverlay.classList.add('active');
            }
        } else {
            // Analyze mode — route the document text + special
            // instructions through the normal chat pipeline. We've
            // already added the user bubble above, so suppress the
            // default user-bubble creation in sendMessageWithAttachments.
            const prompt = _buildAnalyzePrompt(data.filename, data.text, instructions);
            await sendMessageWithAttachments(prompt, [], { skipUserBubble: true });
        }
    } catch (e) {
        console.error('[Ingest] submit failed:', e);
        _setError(e.message || 'Unexpected error during upload.');
    } finally {
        submitBtn.disabled = !selectedFile;
        cancelBtn.disabled = false;
        submitBtn.textContent = originalLabel;
    }
}

function _buildAnalyzePrompt(filename, text, instructions) {
    // The document text is already in the previous user message (saved
    // by /document/extract right before this call), so the analyze prompt
    // only needs to reference it and add any special instructions. The
    // LLM sees both messages in conversation history.
    const ref = `(Re: document ${filename || 'document'} above.)`;
    const ask = instructions
        ? instructions
        : 'Please analyze this document and offer your commentary.';
    return `${ref} ${ask}`;
}

function _assistantName() {
    // Minimal fallback — the chat module has a richer resolver but we
    // don't need agent/collection title display logic here. The header
    // we set will be replaced on conversation reload by the loader's
    // resolved name.
    return state.loadedAgent || state.loadedCollectionTitle || state.loadedCollection || 'Assistant';
}
